import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, CheckCircle2 } from 'lucide-react';
import { authApi } from '../api';

const ResetPassword: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await authApi.confirmPasswordReset(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'This link is invalid or has expired');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell bg-gradient-to-br from-green-950 via-green-900 to-black flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-black/40 backdrop-blur-md rounded-3xl p-6 shadow-2xl border border-green-800/50">
          <h1 className="text-2xl font-bold text-white mb-1">Set a new password</h1>
          <p className="text-sm text-green-300/80 mb-5">Choose something you'll remember this time. 😉</p>

          {done ? (
            <div className="text-center py-4">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-green-100">Password updated. Taking you to sign in…</p>
            </div>
          ) : !token ? (
            <p className="text-red-300 text-sm">This reset link is missing its token. Request a new one from the sign-in page.</p>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="bg-red-500/15 text-red-300 border border-red-500/30 p-3 rounded-xl text-sm text-center">{error}</div>}
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-300/80 pointer-events-none" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  placeholder="New password (min 8 chars)"
                  className="w-full pl-11 pr-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:bg-white/15 focus:ring-2 focus:ring-yellow-500/70 outline-none transition"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3.5 rounded-xl font-semibold hover:from-green-500 hover:to-emerald-500 transition disabled:opacity-50"
              >
                {loading ? 'Updating…' : 'Update password'}
              </button>
            </form>
          )}

          <Link to="/login" className="mt-5 block text-center text-sm text-green-300/80 hover:text-white">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
